import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCheck, Search, XCircle } from 'lucide-react';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { Toolbar } from '@vnetwork/ui/layout';
import { useWaitingConfirmServices, type ServiceRow } from '@/api/services';
import { useConfirmServiceInList, useRejectServiceInList } from '@/api/services-mutations';
import { serviceColumns } from '../list-configs/service-column-definitions';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TabLoading } from '@/pages/customers/detail/tab-states';

const PAGE_SIZE = 20;

/** Debounce a fast-changing value (search box) to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

const svcLabel = (r: ServiceRow) => `${r.product_group_name ?? ''} - ${r.service_name}`.replace(/^ - /, '');

/** A service can only be confirmed once it has at least one sale-tracking owner. */
const hasSaleTracking = (r: ServiceRow) => (r.employee_sale_tracking_ids?.length ?? 0) > 0;

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

/**
 * Waiting-confirmation list — the primary confirm workflow (mirrors the legacy
 * `WaitingConfirm` list). A selectable, server-paginated table whose rows carry
 * inline Confirm / Reject actions and a bulk "Confirm selected" bar. Confirm is
 * gated on the service having a sale-tracking owner (backend rule); reject takes
 * a reason. Row-click still opens the detail page.
 */
export function WaitingConfirmListSection({ onRowClick }: { onRowClick?: (row: ServiceRow) => void }) {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [confirmTargets, setConfirmTargets] = useState<ServiceRow[] | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ServiceRow | null>(null);

  const { data, isLoading, isError, isFetching, refetch } = useWaitingConfirmServices({
    page,
    page_size: PAGE_SIZE,
    q: query || undefined,
  });
  const confirm = useConfirmServiceInList();
  const reject = useRejectServiceInList();

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? rows.length;
  const pageCount = pagination?.total_pages ?? 1;

  const selectedRows = useMemo(
    () => rows.filter((r) => selection[String(r.service_id)]),
    [rows, selection],
  );

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  // Open the confirm dialog for the given rows, blocking any that lack a
  // sale-tracking owner (matches the legacy guard) with a naming toast.
  const askConfirm = (targets: ServiceRow[]) => {
    const missing = targets.filter((r) => !hasSaleTracking(r));
    if (missing.length) {
      toast.error(`Assign a sale tracking owner first: ${missing.map(svcLabel).join(', ')}`);
      return;
    }
    setConfirmTargets(targets);
  };

  const runConfirm = async () => {
    if (!confirmTargets) return;
    const results = await Promise.allSettled(
      confirmTargets.map((r) => confirm.mutateAsync(r.service_id)),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed) toast.error(`${failed} of ${confirmTargets.length} service(s) could not be confirmed.`);
    else toast.success(confirmTargets.length > 1 ? `${confirmTargets.length} services confirmed` : 'Service confirmed');
    setSelection({});
    setConfirmTargets(null);
  };

  const runReject = (message: string) => {
    if (!rejectTarget) return;
    reject.mutate(
      { serviceId: rejectTarget.service_id, message },
      {
        onSuccess: () => { toast.success('Service rejected'); setRejectTarget(null); },
        onError: (e) => toast.error(errMsg(e)),
      },
    );
  };

  const columns = useMemo<ColumnDef<ServiceRow, unknown>[]>(
    () => [
      ...(serviceColumns as ColumnDef<ServiceRow, unknown>[]),
      {
        id: 'confirm_actions',
        header: () => <span style={{ display: 'block', textAlign: 'right' }}>Actions</span>,
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 'var(--vn-space-1)', justifyContent: 'flex-end' }}>
            <Button
              size="sm"
              variant="primary"
              onClick={(e) => { e.stopPropagation(); askConfirm([row.original]); }}
            >
              <CheckCircle2 size={14} /> Confirm
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={(e) => { e.stopPropagation(); setRejectTarget(row.original); }}
            >
              <XCircle size={14} /> Reject
            </Button>
          </div>
        ),
      },
    ],
    // askConfirm/setRejectTarget are stable enough for the row cells.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const confirmCount = confirmTargets?.length ?? 0;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={rawQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search services awaiting confirmation…"
            startSlot={<Search size={15} />}
            style={{ maxWidth: 320 }}
          />
        }
        end={
          selectedRows.length > 0 ? (
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                {selectedRows.length} selected
              </span>
              <Button size="sm" variant="primary" onClick={() => askConfirm(selectedRows)}>
                <ClipboardCheck size={15} /> Confirm selected
              </Button>
            </div>
          ) : null
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load services"
          actions={<Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>}
        />
      ) : isLoading ? (
        <TabLoading rows={8} />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.service_id)}
          enableSelection
          rowSelection={selection}
          onRowSelectionChange={setSelection}
          onRowClick={onRowClick}
          emptyState={
            <EmptyState
              icon={<ClipboardCheck size={24} />}
              title="No services awaiting confirmation"
              description="New services pending confirmation will show up here."
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={`${view.summary}${isFetching ? ' …' : ''}`}
              />
            );
          })()}
        />
      )}

      <ConfirmDialog
        open={confirmTargets !== null}
        onOpenChange={(o) => !o && !confirm.isPending && setConfirmTargets(null)}
        title={confirmCount > 1 ? 'Confirm services' : 'Confirm service'}
        description={
          confirmCount > 1
            ? `Confirm ${confirmCount} selected services? Each moves forward in its lifecycle.`
            : `Confirm "${confirmTargets?.[0] ? svcLabel(confirmTargets[0]) : ''}"? This moves it forward in its lifecycle.`
        }
        confirmLabel="Confirm"
        loading={confirm.isPending}
        onConfirm={runConfirm}
      />

      <RejectDialog
        target={rejectTarget}
        pending={reject.isPending}
        onClose={() => setRejectTarget(null)}
        onSubmit={runReject}
      />
    </div>
  );
}

function RejectDialog({
  target,
  pending,
  onClose,
  onSubmit,
}: {
  target: ServiceRow | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [text, setText] = useState('');
  useEffect(() => { if (target) setText(''); }, [target]);
  if (!target) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject service</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ color: 'var(--vn-fg-muted)', marginTop: 'var(--vn-space-0)' }}>{svcLabel(target)}</p>
          <Field label="Reason" required>
            {(p) => <Input {...p} value={text} onChange={(e) => setText(e.target.value)} placeholder="Why is this service rejected?" autoFocus />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>Close</Button>
          <Button variant="danger" disabled={!text.trim() || pending} onClick={() => onSubmit(text.trim())}>Reject</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
